import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { queryClient } from "api/tanStackQuery/queryClient";
import {
  GlobalEventResponse,
  globalEventSchema,
  globalEventsResponseSchema,
} from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { useQueryWithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { GlobalEventType } from "shared/types/operationalInformation";
import { useParseResultContext } from "shared/utils/useParseResult";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  GlobalInformationMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

const updateGlobalInfo = (
  message: GlobalEventResponse,
  prev: GlobalEventResponse[],
) => {
  if (!message.isVisible) {
    return prev.filter((info) => info.uuid !== message.uuid);
  }
  return [message, ...prev.filter((info) => info.uuid !== message.uuid)];
};

export const globalEventsQueryKey = (countryCode: string) => [
  "globalEvents",
  countryCode,
];

const globalEventsType: GlobalEventType = "GLOBAL_GENERAL_WARNING";

const messageHandler = ({ message }: GlobalInformationMessage) => {
  const globalInfoMessage = globalEventSchema.parse(message);
  queryClient.setQueryData<GlobalEventResponse[]>(
    globalEventsQueryKey(globalInfoMessage.countryCode),
    (prev) => {
      if (!prev) {
        return globalInfoMessage.isVisible ? [globalInfoMessage] : [];
      }
      return updateGlobalInfo(globalInfoMessage, prev);
    },
  );
};

export const useGlobalInformation = () => {
  const { handleParse } = useParseResultContext();
  const parse = handleParse(
    (response: GlobalEventResponse) => [
      response.customTexts[0]?.texts.NOB?.title ?? "Ingen tittel",
    ],
    "global",
  );
  const countryCode = useCountryCodeForDropsRole();

  const globalEvents = useQueryWithInternalMessages(
    useQuery({
      queryKey: globalEventsQueryKey(countryCode),
      staleTime: 0,
      queryFn: ({ signal }) =>
        axiosClient
          .get<GlobalEventResponse[]>(
            `${getBackendUrl()}/global-information/visible/${countryCode}?type=${globalEventsType}`,
            {
              signal,
            },
          )
          .then((res) => parse(globalEventsResponseSchema, res.data)),
    }),
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.GLOBAL_INFORMATION]: messageHandler,
  });

  return { globalEvents };
};
